/**
 * ViewAllWorkersPage.js — Complete worker roster (active + inactive)
 *
 * Mirrors the HomePage shell (same .app layout, .app-header, left/right columns)
 * but swaps the live attendance WebSocket for a one-shot worker API call.
 *
 * Left column  — Stats snapshot: active count, inactive count, outstanding dues
 * Right column — Searchable, filterable, sortable list of all worker records
 *
 * Primary action on each card: "View Profile" → /worker/:id
 * Secondary actions: Call (tel: link), Pay advance (PaymentModal, manager only)
 */

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { showAlert } from '../components/UI';
import PaymentModal from '../components/PaymentModal';
import { getAllWorkersWithDetails } from '../api/directus';

const BASE_URL = process.env.REACT_APP_DIRECTUS_URL || 'https://staff.innomalous.com';

// ── Module-level helpers ───────────────────────────────────────────────────────

/**
 * Build a sized, authenticated Directus asset URL.
 * Mirrors the same helper in HomePage — dev mode appends the access token as a
 * query param because cookies don't cross origins to staff.innomalous.com from localhost.
 */
function buildPhotoUrl(photo, px = 80) {
  if (!photo) return null;
  const uuid = typeof photo === 'string' ? photo : photo.id;
  if (!uuid) return null;
  const url = `${BASE_URL}/assets/${uuid}?width=${px}&height=${px}&fit=cover`;
  if (process.env.NODE_ENV === 'development') {
    const token = localStorage.getItem('dev_access_token');
    if (token) return `${url}&access_token=${token}`;
  }
  return url;
}

/** Derive initials from a name string (same logic as HomePage). */
function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * Map a worker name to a hue value (0–359) for deterministic avatar colours.
 * Same polynomial rolling hash as HomePage so the same worker always gets the
 * same colour regardless of which page they appear on.
 */
function nameToHue(name) {
  let hash = 0;
  for (const c of (name || '')) hash = (hash * 31 + c.charCodeAt(0)) & 0xffff;
  return hash % 360;
}

/**
 * Compact "Mon YYYY" date format for the tenure strip (e.g. "Jan 2024").
 * Shorter than the full-date format used elsewhere so two dates + arrow fit inline.
 */
function fmtDateShort(iso) {
  if (!iso) return null;
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const [y, m] = iso.split('-');
  return `${months[parseInt(m, 10) - 1]} ${y}`;
}

/**
 * Human-readable tenure length from a start ISO date to an end ISO date (or today).
 * Returns strings like "2 yr 3 mo", "6 mo", "< 1 mo".
 */
function tenureDuration(startIso, endIso) {
  if (!startIso) return '';
  const start = new Date(startIso);
  const end   = endIso ? new Date(endIso) : new Date();
  let months  = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
  if (months < 0) months = 0;
  const years = Math.floor(months / 12);
  const rem   = months % 12;
  if (years > 0 && rem > 0) return `${years} yr ${rem} mo`;
  if (years > 0) return `${years} yr`;
  if (months >= 1) return `${months} mo`;
  return '< 1 mo';
}

/**
 * Build a chronological list of all tenures for a worker.
 * History records (past tenures) come first, the current worker record last.
 * Each entry: { doj, doe, exitReason, isCurrent }
 */
function buildTenures(worker) {
  const past = (worker.history || [])
    .slice()
    .sort((a, b) => (a.doj > b.doj ? 1 : -1))
    .map(h => ({ doj: h.doj, doe: h.doe, exitReason: h.exit_reason, isCurrent: false }));
  const current = { doj: worker.doj, doe: worker.doe, exitReason: worker.exit_reason, isCurrent: true };
  return [...past, current];
}

// ── Sub-components ─────────────────────────────────────────────────────────────
// Defined at module scope (not inside the page component) so React sees a stable
// component type across re-renders — avoids unmount→remount on every state change.

/**
 * Small count chip — mirrors ChipStat from HomePage for visual consistency
 * in the snapshot section.
 */
function ChipStat({ label, count, dot }) {
  return (
    <div className="chip">
      <div className={`chip-dot ${dot}`} />
      <div className="chip-num num">{count}</div>
      <span className="chip-label">{label}</span>
    </div>
  );
}

/**
 * Sort dropdown — mirrors SortMenu from HomePage.
 * Rendered via portal to escape overflow clipping on the toolbar container.
 */
function SortMenu({ sort, onSort }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos]   = useState(null);
  const btnRef  = useRef(null);
  const menuRef = useRef(null);

  function toggle() {
    const rect = btnRef.current.getBoundingClientRect();
    setPos({ top: rect.bottom + 6, right: window.innerWidth - rect.right });
    setOpen(o => !o);
  }

  useEffect(() => {
    if (!open) return;
    const fn = (e) => {
      if (!menuRef.current?.contains(e.target) && !btnRef.current?.contains(e.target))
        setOpen(false);
    };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, [open]);

  const labels = { name: 'Name', balance: 'Balance', status: 'Status' };
  return (
    <>
      <button ref={btnRef} className="sort-btn" onClick={toggle}>
        <span className="material-symbols-rounded">sort</span>
        <span>{labels[sort]}</span>
      </button>
      {open && pos && ReactDOM.createPortal(
        <div
          ref={menuRef}
          className="dash-popover"
          style={{ position: 'fixed', top: pos.top, right: pos.right, minWidth: 160, zIndex: 500 }}
        >
          {Object.entries(labels).map(([k, label]) => (
            <button key={k} onClick={() => { onSort(k); setOpen(false); }}>
              <span
                className="material-symbols-rounded"
                style={{ opacity: sort === k ? 1 : 0 }}
              >check</span>
              {label}
            </button>
          ))}
        </div>,
        document.body
      )}
    </>
  );
}

/**
 * Row-based worker entry for the all-workers roster.
 *
 * The entire row is the primary action — clicking anywhere navigates to the
 * worker profile. Secondary actions (Call, Pay advance) are tucked into a
 * quiet kebab (⋮) menu so the list stays clean and scannable.
 */
function WorkerRow({ worker, onPay, isManager, navigate }) {
  const { pathname } = useLocation();
  const photoUrl  = buildPhotoUrl(worker.photo);
  const initials  = getInitials(worker.name);
  const hue       = nameToHue(worker.name);
  const balValue  = worker.current_balance || 0;
  const isZero    = balValue <= 0;
  const bal       = Math.round(Math.abs(balValue)).toLocaleString('en-IN');
  const isInactive = worker.status !== 'active';

  const [kebabOpen, setKebabOpen] = useState(false);
  const [kebabPos,  setKebabPos]  = useState(null);
  const kebabBtnRef  = useRef(null);
  const kebabMenuRef = useRef(null);

  useEffect(() => {
    if (!kebabOpen) return;
    const fn = (e) => {
      if (!kebabMenuRef.current?.contains(e.target) && !kebabBtnRef.current?.contains(e.target))
        setKebabOpen(false);
    };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, [kebabOpen]);

  function openKebab(e) {
    e.stopPropagation();
    const rect = kebabBtnRef.current.getBoundingClientRect();
    setKebabPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
    setKebabOpen(o => !o);
  }

  const teamName   = worker.team?.name         || '';
  const shiftName  = worker.shift?.name        || '';
  const rateName   = worker.rate?.name         || '';
  const policyName = worker.leave_policy?.name || '';
  const tenures    = buildTenures(worker);

  function goToProfile() {
    navigate(`/worker/${worker.id}`, { state: { from: pathname } });
  }

  return (
    <div
      className={`rrow${isInactive ? ' is-inactive' : ''}`}
      onClick={goToProfile}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter') goToProfile(); }}
    >
      {/* Avatar */}
      <div
        className="wk-avatar"
        style={photoUrl ? {} : {
          background: `oklch(86% 0.045 ${hue})`,
          color:      `oklch(32% 0.07 ${hue})`,
        }}
      >
        {photoUrl
          ? <img src={photoUrl} alt={worker.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          : initials
        }
      </div>

      {/* Name · pills — meta column */}
      <div className="rrow-meta">

        {/* Tenure timeline — stacked strips, oldest first, current last */}
        <div className="rrow-tenures">
          {tenures.map((t, i) => {
            const isLast    = i === tenures.length - 1;
            const isActive  = t.isCurrent && !isInactive;
            const startStr  = fmtDateShort(t.doj);
            const endStr    = t.doe ? fmtDateShort(t.doe) : 'Present';
            const dur       = tenureDuration(t.doj, t.doe);
            const reason    = t.exitReason;
            return (
              <div key={i} className={`rrow-tenure${!isLast ? ' is-past' : ''}`}>
                <span className={`rrow-tenure-dot${isActive ? ' is-active' : ''}`} />
                <span>{startStr}</span>
                <span className="rrow-tenure-arrow">→</span>
                <span className={`rrow-tenure-to${isActive ? ' is-active' : ''}`}>{endStr}</span>
                {dur && <span className="rrow-tenure-dur">{dur}</span>}
                {reason && <span className="rrow-tenure-reason">{reason}</span>}
              </div>
            );
          })}
        </div>

        {/* Worker name */}
        <h3 className="rrow-name">{worker.name}</h3>

        {/* Row 3: team / shift / rate / leave policy as compact tags */}
        <div className="rrow-tags">
          {teamName   && <span className="rrow-tag">{teamName}</span>}
          {shiftName  && <span className="rrow-tag">{shiftName}</span>}
          {rateName   && <span className="rrow-tag">{rateName}</span>}
          {policyName && <span className="rrow-tag">{policyName}</span>}
        </div>
      </div>

      {/* Status pill + balance */}
      <div className="rrow-right">
        <span className={`pill ${isInactive ? 'pill-inactive' : 'pill-active'}`}>
          <span className="material-symbols-rounded">
            {isInactive ? 'do_not_disturb_on' : 'check_circle'}
          </span>
          {isInactive ? 'Inactive' : 'Active'}
        </span>
        <span className={`rrow-bal${isZero ? ' rrow-bal-zero' : ''}`}>
          {isZero ? '—' : `₹${bal}`}
        </span>
      </div>

      {/* Kebab — stopPropagation prevents the row click from firing */}
      <div onClick={e => e.stopPropagation()} onKeyDown={e => e.stopPropagation()}>
        <button
          ref={kebabBtnRef}
          className="rrow-kebab"
          aria-label="More actions"
          aria-expanded={kebabOpen}
          onClick={openKebab}
        >
          <span className="material-symbols-rounded">more_vert</span>
        </button>
        {kebabOpen && kebabPos && ReactDOM.createPortal(
          <div
            ref={kebabMenuRef}
            className="dash-popover"
            style={{ position: 'fixed', top: kebabPos.top, right: kebabPos.right, minWidth: 180, zIndex: 500 }}
          >
            <button onClick={() => { setKebabOpen(false); goToProfile(); }}>
              <span className="material-symbols-rounded">person</span>
              Open profile
            </button>
            <button
              disabled={!worker.phone}
              title={worker.phone ? undefined : 'No phone number on file'}
              onClick={() => { setKebabOpen(false); if (worker.phone) window.location.href = `tel:${worker.phone}`; }}
            >
              <span className="material-symbols-rounded">call</span>
              {worker.phone ? 'Call' : 'Call (no number)'}
            </button>
            <button
              disabled={!isManager}
              title={isManager ? undefined : 'Manager access required'}
              onClick={() => { setKebabOpen(false); onPay(worker); }}
            >
              <span className="material-symbols-rounded">currency_rupee</span>
              Pay advance
            </button>
          </div>,
          document.body
        )}
      </div>
    </div>
  );
}

// ── Main page component ────────────────────────────────────────────────────────

export default function ViewAllWorkersPage() {
  const { currentUser, logout, isManager } = useAuth();
  const navigate = useNavigate();

  const [workers, setWorkers]           = useState([]);
  const [loading, setLoading]           = useState(true);
  const [searchText, setSearchText]     = useState('');
  const [filter, setFilter]             = useState('all');
  const [sort, setSort]                 = useState('status');
  const [navOpen, setNavOpen]           = useState(false);
  const [paymentTarget, setPaymentTarget] = useState(null);

  // Fetch all workers on mount
  const loadWorkers = useCallback(() => {
    setLoading(true);
    getAllWorkersWithDetails()
      .then(res => setWorkers(res.data || []))
      .catch(() => showAlert('Failed to load workers', 'error'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadWorkers(); }, [loadWorkers]);

  // Derived stats (memo — no extra API calls)
  const stats = useMemo(() => {
    const active   = workers.filter(w => w.status === 'active').length;
    const inactive = workers.filter(w => w.status !== 'active').length;
    // Outstanding dues = total positive balances (amounts we owe to workers)
    const dues     = Math.round(workers.reduce((sum, w) => sum + Math.max(0, w.current_balance || 0), 0));
    return { active, inactive, dues };
  }, [workers]);

  // Filtered + sorted list (memo — recomputes only when dependencies change)
  const filtered = useMemo(() => {
    let list = workers;
    if (filter === 'active')   list = list.filter(w => w.status === 'active');
    if (filter === 'inactive') list = list.filter(w => w.status !== 'active');

    const q = searchText.trim().toLowerCase();
    if (q.length >= 1)
      list = list.filter(w => w.name?.toLowerCase().includes(q) || w.phone?.includes(q));

    return [...list].sort((a, b) => {
      if (sort === 'name')    return (a.name || '').localeCompare(b.name || '');
      if (sort === 'balance') return (b.current_balance || 0) - (a.current_balance || 0);
      // status: active first, then alphabetical within the same status
      if (a.status !== b.status) return a.status === 'active' ? -1 : 1;
      return (a.name || '').localeCompare(b.name || '');
    });
  }, [workers, filter, searchText, sort]);

  const filterItems = [
    { key: 'all',      label: 'All',      count: workers.length },
    { key: 'active',   label: 'Active',   count: stats.active   },
    { key: 'inactive', label: 'Inactive', count: stats.inactive  },
  ];

  return (
    <div className="app">

      {/* ── Header ── */}
      <header className="app-header">
        <div className="greet" style={{ alignItems: 'center' }}>
          <button className="icon-btn" onClick={() => navigate('/')} title="Back to dashboard">
            <span className="material-symbols-rounded">arrow_back</span>
          </button>
          <div className="greet-text">
            <h1>Workers</h1>
            <small>{workers.length} total on roster</small>
          </div>
        </div>
        <div className="header-actions">
          <button className="icon-btn" aria-label="Menu" onClick={() => setNavOpen(true)}>
            <span className="material-symbols-rounded">menu</span>
          </button>
        </div>
      </header>

      {/* ── Left column: stats snapshot ── */}
      <div className="left-col">
        <section className="snapshot fade-up">
          <div className="snapshot-eyebrow">
            <span className="material-symbols-rounded" style={{ fontSize: 14 }}>group</span>
            <span>Worker Roster</span>
          </div>

          {/* Big active count / total */}
          <div className="snapshot-stat">
            <span className="snapshot-num num">{stats.active}</span>
            <span className="snapshot-of num">/ {workers.length}</span>
          </div>
          <p className="snapshot-label">
            <strong>{stats.active} active</strong>
            {stats.inactive > 0 && <> · {stats.inactive} inactive</>}
          </p>

          {/* Active vs inactive proportion bar */}
          {workers.length > 0 && (
            <div className="stack-bar">
              {stats.active > 0 && (
                <span
                  className="c-present"
                  style={{ width: `${(stats.active / workers.length) * 100}%` }}
                  title={`Active: ${stats.active}`}
                />
              )}
              {stats.inactive > 0 && (
                <span
                  className="c-absent"
                  style={{ width: `${(stats.inactive / workers.length) * 100}%` }}
                  title={`Inactive: ${stats.inactive}`}
                />
              )}
            </div>
          )}

          {/* Three chips: active, inactive, outstanding dues */}
          <div className="chips waw-chips">
            <ChipStat label="Active"   count={stats.active}   dot="c-present" />
            <ChipStat label="Inactive" count={stats.inactive} dot="c-absent"  />
            {/* Dues chip — uses a smaller font so the ₹ amount fits in the chip */}
            <div className="chip">
              <div className="chip-dot c-ended" />
              <div className="chip-num num waw-dues-num">
                ₹{stats.dues.toLocaleString('en-IN')}
              </div>
              <span className="chip-label">Outstanding</span>
            </div>
          </div>
        </section>
      </div>

      {/* ── Right column: worker list ── */}
      <div className="right-col">

        {/* Search + sort toolbar */}
        <div className="dash-toolbar fade-up" style={{ animationDelay: '0.05s' }}>
          <div className="dash-search">
            <span className="material-symbols-rounded">search</span>
            <input
              placeholder="Search worker…"
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
            />
            {searchText && (
              <button
                className="icon-btn"
                style={{ width: 28, height: 28, border: 0 }}
                onClick={() => setSearchText('')}
              >
                <span className="material-symbols-rounded">close</span>
              </button>
            )}
          </div>
          <SortMenu sort={sort} onSort={setSort} />
        </div>

        {/* Filter bar */}
        <div className="dash-filters fade-up" style={{ animationDelay: '0.08s' }}>
          {filterItems.map(f => (
            <button
              key={f.key}
              className="dash-filter"
              aria-pressed={filter === f.key}
              onClick={() => setFilter(f.key)}
            >
              <span>{f.label}</span>
              <span className="fcount num">{f.count}</span>
            </button>
          ))}
        </div>

        {/* List header */}
        <div className="list-meta">
          <h2>
            {filter === 'all'      ? 'All Workers'      :
             filter === 'active'   ? 'Active Workers'   : 'Inactive Workers'}
          </h2>
          <span className="list-count num">{filtered.length} of {workers.length}</span>
        </div>

        {/* Worker cards */}
        {loading ? (
          <div className="dash-empty">
            <span className="material-symbols-rounded">sync</span>
            <p>Loading workers…</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="dash-empty">
            <span className="material-symbols-rounded">person_search</span>
            <p>No workers match your filter.</p>
          </div>
        ) : (
          <div className="roster-list">
            {filtered.map(worker => (
              <WorkerRow
                key={worker.id}
                worker={worker}
                onPay={setPaymentTarget}
                isManager={isManager}
                navigate={navigate}
              />
            ))}
          </div>
        )}
      </div>

      {/* ── Navigation modal (hamburger menu) ── */}
      {navOpen && (
        <div className="modal-overlay" onMouseDown={() => setNavOpen(false)}>
          <div className="modal modal-sm" onMouseDown={e => e.stopPropagation()}>
            <div className="modal-header">
              Menu
              <button className="btn-icon" onClick={() => setNavOpen(false)}>✕</button>
            </div>
            <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button
                className="btn btn-ghost"
                style={{ justifyContent: 'flex-start' }}
                onClick={() => { navigate('/'); setNavOpen(false); }}
              >
                <span className="material-symbols-rounded">home</span>
                Dashboard
              </button>
              <button
                className="btn btn-primary"
                style={{ justifyContent: 'flex-start' }}
                onClick={() => { navigate('/new-worker'); setNavOpen(false); }}
              >
                + Add New Worker
              </button>
              <button
                className="btn btn-ghost"
                style={{ justifyContent: 'flex-start' }}
                onClick={() => { logout(); setNavOpen(false); }}
              >
                Log Out
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment modal — re-fetches workers after save to update displayed balances */}
      <PaymentModal
        open={!!paymentTarget}
        workerId={paymentTarget?.id}
        workerName={paymentTarget?.name}
        onClose={() => setPaymentTarget(null)}
        onSaved={loadWorkers}
      />
    </div>
  );
}
